import type { User } from "~/models/User";
import type { UserRole } from "~/models/User";
import type { ApiResponse } from "~/types/ApiResponse";

const EMPTY_USER: Omit<User, "account"> & { account: any } = {
    id: 0,
    avatar: undefined,
    domain_id: 0,
    first_name: "",
    last_name: "",
    phone: "",
    name: "",
    email: "",
    account: {},
    account_id: 0,
    account_name: "",
    is_super_admin: false,
    role: "guest",
    roles: [],
    permissions: [],
    email_verified_at: "",
    created_at: "",
    updated_at: "",
    deleted_at: undefined,
};

export interface PermissionCheckOptions {
    level: "user" | "account";
    with_super_admin: boolean;
    user: User;
}

export interface UseUser {
    // The authorized user
    user: ComputedRef<User>;

    // The authorized user can manage templates
    canManageTemplates: ComputedRef<boolean>;

    // The authorized user is a super admin
    isSuperAdmin: ComputedRef<boolean>;

    // The authorized user is in a partner account
    isPartnerAccount: ComputedRef<boolean>;

    // The authorized user is a partner user
    isPartnerUser: ComputedRef<boolean>;

    // The authorized user is a partner manager
    isPartnerManager: ComputedRef<boolean>;

    // Check if the user/account has a specific role
    hasRole(role: UserRole, options?: Partial<PermissionCheckOptions>): boolean;

    // Check if the user/account has all specified roles
    hasAllRoles(
        roles: UserRole[],
        options?: Partial<PermissionCheckOptions>,
    ): boolean;

    // Check if the user/account has any of the specified roles
    hasAnyRoles(
        roles: UserRole[],
        options?: Partial<PermissionCheckOptions>,
    ): boolean;

    // Check if the user/account has a specific permission
    hasPermissionTo(
        permission: string,
        options?: Partial<PermissionCheckOptions>,
    ): boolean;

    // Check if the user/account has all of the specified permissions
    hasPermissionToAll(
        permissions: string[],
        options?: Partial<PermissionCheckOptions>,
    ): boolean;

    // Check if the user/account has any of the specified permissions
    hasPermissionToAny(
        permissions: string[],
        options?: Partial<PermissionCheckOptions>,
    ): boolean;

    // Check if the user/account doesn't have a specific permission
    doesntHavePermissionTo(
        permission: string,
        options?: Partial<PermissionCheckOptions>,
    ): boolean;

    // Check if the user/account doesn't have all of the specified permissions
    doesntHavePermissionToAll(
        permissions: string[],
        options?: Partial<PermissionCheckOptions>,
    ): boolean;

    // Check if the user/account doesn't have any of the specified permissions
    doesntHavePermissionToAny(
        permissions: string[],
        options?: Partial<PermissionCheckOptions>,
    ): boolean;

    // Refresh the user
    refresh: () => void;
}

export function useUser(): UseUser {
    const sanctumUser = useSanctumUser<ApiResponse<User>>();

    const user = computed(
        () => sanctumUser.value?.data ?? (EMPTY_USER as User),
    );

    const isSuperAdmin = computed(() =>
        user.value.roles.includes("super_admin"),
    );

    function getOptions(
        options: Partial<PermissionCheckOptions> = {},
    ): PermissionCheckOptions {
        return {
            level: "user",
            with_super_admin: true,
            ...options,
            user: options.user ?? user.value,
        };
    }

    function getPermissions(level: PermissionCheckOptions["level"], user: User) {
        return (
            (level === "account"
                ? user.account?.permissions
                : user.permissions) ?? []
        );
    }

    function hasRole(
        role: UserRole,
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, user } = getOptions(options);
        if (with_super_admin && isSuperAdmin.value) {
            return true;
        }

        return user.roles.includes(role);
    }

    function hasAllRoles(
        roles: UserRole[],
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, user } = getOptions(options);
        if (with_super_admin && isSuperAdmin.value) {
            return true;
        }

        for (const role of roles) {
            if (!user.roles.includes(role)) return false;
        }

        return true;
    }

    function hasAnyRoles(
        roles: UserRole[],
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, user } = getOptions(options);
        if (with_super_admin && isSuperAdmin.value) {
            return true;
        }

        for (const role of roles) {
            if (user.roles.includes(role)) return true;
        }

        return false;
    }

    function hasPermissionTo(
        permission: string,
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, level, user } = getOptions(options);

        return (
            (with_super_admin && isSuperAdmin.value) ||
            getPermissions(level, user).includes(permission)
        );
    }

    function hasPermissionToAll(
        permissions: string[],
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, level, user } = getOptions(options);
        if (with_super_admin && isSuperAdmin.value) {
            return true;
        }

        for (const permission of permissions) {
            if (!getPermissions(level, user).includes(permission)) return false;
        }

        return true;
    }

    function hasPermissionToAny(
        permissions: string[],
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, level, user } = getOptions(options);
        if (with_super_admin && isSuperAdmin.value) {
            return true;
        }

        for (const permission of permissions) {
            if (getPermissions(level, user).includes(permission)) return true;
        }

        return false;
    }

    function doesntHavePermissionTo(
        permission: string,
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, level, user } = getOptions(options);
        return (
            (!with_super_admin || !isSuperAdmin.value) &&
            !getPermissions(level, user).includes(permission)
        );
    }

    function doesntHavePermissionToAll(
        permissions: string[],
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, level, user } = getOptions(options);
        if (with_super_admin && isSuperAdmin.value) {
            return false;
        }

        for (const permission of permissions) {
            if (!getPermissions(level, user).includes(permission)) return true;
        }

        return false;
    }

    function doesntHavePermissionToAny(
        permissions: string[],
        options: Partial<PermissionCheckOptions> = {},
    ): boolean {
        const { with_super_admin, level, user } = getOptions(options);
        if (with_super_admin && isSuperAdmin.value) {
            return false;
        }

        for (const permission of permissions) {
            if (!getPermissions(level, user).includes(permission)) return true;
        }

        return false;
    }

    const canManageTemplates = computed(() => {
        return hasPermissionTo("system.templates.manage", {
            level: "account",
            with_super_admin: false,
        });
    })

    const isPartnerAccount = computed(() => {
        return user.value.account?.is_partner;
    });

    const isPartnerManager = computed(() => {
        return hasRole("partner_manager", {
            level: "user",
            with_super_admin: false,
        });
    });

    const isPartnerUser = computed(() => {
        return isPartnerManager.value || hasRole("partner_user", {
            level: "user",
            with_super_admin: false,
        });
    });

    return {
        user,
        isSuperAdmin,
        isPartnerAccount,
        isPartnerManager,
        isPartnerUser,
        canManageTemplates,
        refresh: () => useAuth()?.refreshIdentity(),
        hasRole,
        hasAnyRoles,
        hasAllRoles,
        hasPermissionTo,
        hasPermissionToAny,
        hasPermissionToAll,
        doesntHavePermissionTo,
        doesntHavePermissionToAny,
        doesntHavePermissionToAll,
    };
}
